/**
 * Doctor Consultation History.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { consultationAPI } from '../../services/api';
import Layout from '../../components/Layout';
import toast from 'react-hot-toast';
import { useNotifications } from '../../contexts/NotificationsContext';

const DoctorConsultations = () => {
  const navigate = useNavigate();
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState('');
  const { on } = useNotifications();

  const loadConsultations = useCallback(async () => {
    try {
      const res = await consultationAPI.history();
      setConsultations(Array.isArray(res.data) ? res.data : (res.data?.consultations || []));
    } catch {
      toast.error('Failed to load consultations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConsultations();
  }, [loadConsultations]);

  useEffect(() => on('appointment_update', loadConsultations), [loadConsultations, on]);

  const completeConsultation = async (consultation) => {
    setProcessingId(consultation.id);
    try {
      await consultationAPI.end({ room_id: consultation.room_id, notes: consultation.doctor_notes || '' });
      toast.success('Consultation marked as done.');
      await loadConsultations();
    } catch (err) {
      toast.error(err?.message || 'Unable to complete consultation.');
    } finally {
      setProcessingId('');
    }
  };

  const getStatusClasses = (status) => {
    if (status === 'completed') return 'bg-green-100 text-green-800';
    if (status === 'active' || status === 'in_progress') return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getIconClasses = (status) => {
    if (status === 'completed') return 'bg-green-100 text-green-600';
    if (status === 'active' || status === 'in_progress') return 'bg-blue-100 text-blue-600';
    return 'bg-gray-100 text-gray-600';
  };

  return (
    <Layout title="Consultations" role="doctor" showBack>
      <div className="p-4 pb-24">
        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => <div key={i} className="skeleton h-20 rounded-2xl" />)}
          </div>
        ) : consultations.length === 0 ? (
          <div className="card text-center py-12 text-gray-400">
            <svg className="w-16 h-16 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <p className="font-medium">No consultations yet</p>
            <p className="text-sm mt-1">Your consultation history will appear here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {consultations.map(c => (
              <div key={c.id} className="card hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${getIconClasses(c.status).split(' ').slice(0, 1).join(' ')}`}>
                    <svg className={`w-5 h-5 ${getIconClasses(c.status).split(' ').slice(1).join(' ')}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{c.patient_name || 'Patient'}</p>
                    <p className="text-xs text-gray-500">{c.created_at ? new Date(c.created_at).toLocaleString() : ''}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusClasses(c.status)}`}>
                    {(c.status || '').replace('_', ' ')}
                  </span>
                </div>
                {(c.doctor_notes || c.notes) && <p className="text-xs text-gray-500 mt-2 line-clamp-2">{c.doctor_notes || c.notes}</p>}
                {c.status !== 'completed' && c.room_id && (
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      onClick={() => navigate(`/consultation?room=${c.room_id}&appointment=${c.appointment_id || ''}`)}
                      className="px-3 py-1.5 bg-accent-500 text-white text-xs font-medium rounded-lg hover:bg-accent-600 transition-colors"
                    >
                      Resume
                    </button>
                    <button
                      onClick={() => completeConsultation(c)}
                      disabled={processingId === c.id}
                      className="px-3 py-1.5 bg-green-500 text-white text-xs font-medium rounded-lg hover:bg-green-600 disabled:opacity-60 transition-colors"
                    >
                      {processingId === c.id ? 'Saving...' : 'Mark Done'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DoctorConsultations;
